import { Minimize2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type {
  FrontPiecesView,
  FrontPieceView,
  FrontRoomsView,
  FrontRoomView,
} from '@obraplan/core/application/contracts/takeoff';
import { err, ok } from '@obraplan/core/domain/shared/result';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { frenteNaFila } from '@/app/outbox-types';
import { GUARDADO_NO_APARELHO } from '@/features/planning/fila-da-conferencia';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { cn } from '@/lib/cn';
import { useCommand } from '@/lib/use-command';
import { PlantaPintada } from './PlantaPintada';
import {
  chaveDaPeca,
  chaveDoComodo,
  estadoDaPeca,
  estadoDoComodo,
  nomeDoApto,
  percentual,
  resumoDasPecas,
} from './planta-pintada';
import { lerPercentual } from './rascunho-dos-comodos';
import type { FundoDaPlanta } from './use-prancha-da-frente';

/**
 * A planta do pavimento em tela cheia, para marcar nela (pedido de 19/10/2026).
 *
 * A planta ocupa a tela. Cada clique põe o cômodo ou a peça na escolha (ou tira), e arrastar um
 * retângulo sobre o fundo escolhe tudo o que tiver o centro dentro dele. O painel ao lado aplica
 * aos escolhidos, de uma vez, o percentual do dia e o "não se aplica" — ou marca as peças como
 * executadas hoje. Grava pelo mesmo caminho do apontamento do dia (`RecordRoomProgress`,
 * `SetRoomNotApplicable`, `RecordPieceExecutions`), com a permissão dele (`labor.confirm`): sem
 * ela, a tela cheia só mostra.
 *
 * A marca da peça e o percentual do cômodo são o avanço físico (D8): a quantidade produzida no
 * dia continua no apontamento.
 */
export interface PlantaEmTelaCheiaProps {
  readonly activityId: string;
  readonly serviceId: string;
  readonly label: string;
  /** AAAA-MM-DD do dia em que a marca é gravada: hoje. */
  readonly date: string;
  readonly rooms?: FrontRoomsView | undefined;
  readonly pieces?: FrontPiecesView | undefined;
  readonly canMark: boolean;
  /** A prancha do desenho, por baixo da planta (`usePranchaDaFrente`). */
  readonly fundo?: FundoDaPlanta | undefined;
  readonly onClose: () => void;
}

const ATALHOS = [0, 25, 50, 75, 100];

function diaEMes(data: string): string {
  return data.split('-').reverse().slice(0, 2).join('/');
}

/** "Sala · Apto 01, Cozinha · Apto 01 e mais 3". */
function nomesEscolhidos(nomes: readonly string[]): string {
  return nomes.length <= 4
    ? nomes.join(', ')
    : `${nomes.slice(0, 4).join(', ')} e mais ${String(nomes.length - 4)}`;
}

export function PlantaEmTelaCheia({
  activityId,
  serviceId,
  label,
  date,
  rooms,
  pieces,
  canMark,
  fundo,
  onClose,
}: PlantaEmTelaCheiaProps) {
  const [escolhidos, setEscolhidos] = useState<ReadonlySet<string>>(new Set());
  const porComodo = rooms?.byRoom === true;

  // Esc fecha; a página inteira vai para a tela cheia do navegador, quando ele deixa.
  useEffect(() => {
    const aoTeclar = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', aoTeclar);
    const raiz = document.documentElement;
    if (document.fullscreenElement === null && typeof raiz.requestFullscreen === 'function') {
      raiz.requestFullscreen().catch(() => undefined);
    }
    return () => {
      window.removeEventListener('keydown', aoTeclar);
      if (document.fullscreenElement !== null && typeof document.exitFullscreen === 'function') {
        document.exitFullscreen().catch(() => undefined);
      }
    };
  }, [onClose]);

  const alternar = (chave: string) =>
    setEscolhidos((antes) => {
      const proximos = new Set(antes);
      if (proximos.has(chave)) proximos.delete(chave);
      else proximos.add(chave);
      return proximos;
    });
  const acrescentar = (chaves: readonly string[]) =>
    setEscolhidos((antes) => new Set([...antes, ...chaves]));
  const limpar = () => setEscolhidos(new Set());

  const comodos = porComodo
    ? (rooms?.rooms ?? []).filter((room) => escolhidos.has(chaveDoComodo(room)))
    : [];
  const pecas = porComodo
    ? []
    : (pieces?.pieces ?? []).filter((piece) => escolhidos.has(chaveDaPeca(piece)));
  const resumo = pieces === undefined ? undefined : resumoDasPecas(pieces.pieces);

  // "Escolher os que faltam": o cômodo não concluído, a peça ainda não executada — só os que
  // estão na planta.
  const faltam = porComodo
    ? (rooms?.rooms ?? [])
        .filter((room) => room.outline !== undefined)
        .filter((room) => {
          const estado = estadoDoComodo(room);
          return estado === 'NAO_INICIADO' || estado === 'EM_ANDAMENTO';
        })
        .map(chaveDoComodo)
    : (pieces?.pieces ?? [])
        .filter((piece) => piece.shape !== undefined && piece.executedOn === undefined)
        .map(chaveDaPeca);
  const quantos = escolhidos.size;
  const nomeDaEscolha = porComodo
    ? quantos === 1
      ? 'cômodo escolhido'
      : 'cômodos escolhidos'
    : quantos === 1
      ? 'peça escolhida'
      : 'peças escolhidas';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Planta em tela cheia: ${label}`}
      className="fixed inset-0 z-50 flex flex-col bg-(--color-surface)"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-(--color-border-subtle) px-4 py-2">
        <div>
          <h2 className="text-base font-semibold">{label}</h2>
          <p className="text-xs text-(--color-content-muted)">
            {porComodo
              ? rooms?.progressFromRooms === undefined
                ? 'Nenhum cômodo apontado ainda.'
                : `Avanço pelos cômodos: ${percentual(rooms.progressFromRooms)}.`
              : resumo !== undefined
                ? `${String(resumo.executadas)} de ${String(resumo.total)} peças executadas.`
                : ''}{' '}
            {canMark
              ? `Clique para escolher um ou vários, ou arraste um retângulo sobre a planta — vale para ${diaEMes(date)}.`
              : 'Só visualização: marcar pede a permissão de conferir a mão de obra.'}
          </p>
        </div>
        <Button type="button" variant="secondary" onClick={onClose}>
          <Minimize2 aria-hidden="true" />
          Sair da tela cheia
        </Button>
      </header>

      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        <div className="min-h-0 flex-1 p-3">
          <PlantaPintada
            rooms={porComodo ? (rooms?.rooms ?? []) : []}
            pieces={porComodo ? [] : (pieces?.pieces ?? [])}
            label={label}
            className="h-full"
            svgClassName="min-h-0 w-full flex-1"
            selected={escolhidos}
            onSelectRoom={(room) => alternar(chaveDoComodo(room))}
            onSelectPiece={(piece) => alternar(chaveDaPeca(piece))}
            onSelectArea={acrescentar}
            fundo={fundo}
          />
        </div>
        <aside className="flex max-h-[45vh] w-full shrink-0 flex-col gap-3 overflow-y-auto border-t border-(--color-border-subtle) p-4 md:max-h-none md:w-80 md:border-t-0 md:border-l">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium" aria-live="polite">
              {quantos === 0 ? 'Nada escolhido' : `${String(quantos)} ${nomeDaEscolha}`}
            </span>
            {faltam.length > 0 && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => acrescentar(faltam)}
              >
                {porComodo ? 'Escolher os que faltam' : 'Escolher as que faltam'} (
                {String(faltam.length)})
              </Button>
            )}
            {quantos > 0 && (
              <Button type="button" size="sm" variant="ghost" onClick={limpar}>
                Limpar
              </Button>
            )}
          </div>
          {comodos.length > 0 ? (
            <MarcarComodos
              key={comodos.map(chaveDoComodo).join(',')}
              rooms={comodos}
              activityId={activityId}
              serviceId={serviceId}
              date={date}
              canMark={canMark}
              onDone={limpar}
            />
          ) : pecas.length > 0 && pieces !== undefined ? (
            <MarcarPecas
              escolhidas={pecas}
              activityId={activityId}
              serviceId={serviceId}
              date={date}
              canMark={canMark}
              onDone={limpar}
            />
          ) : (
            <p className="text-sm text-(--color-content-muted)">
              {porComodo
                ? 'Escolha cômodos na planta para dar o percentual a todos de uma vez.'
                : 'Escolha peças na planta para marcá-las como executadas de uma vez.'}
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}

export function MarcarComodos({
  rooms,
  activityId,
  serviceId,
  date,
  canMark,
  onDone,
}: {
  readonly rooms: readonly FrontRoomView[];
  readonly activityId: string;
  readonly serviceId: string;
  readonly date: string;
  readonly canMark: boolean;
  readonly onDone: () => void;
}) {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const primeiro = rooms[0]!;
  const iguais = rooms.every((room) => room.percent === primeiro.percent);
  const [texto, setTexto] = useState(
    iguais && primeiro.percent !== undefined ? String(primeiro.percent).replace('.', ',') : '',
  );
  // O "não se aplica" só muda se a pessoa mexer: os escolhidos podem estar misturados.
  const todosFora = rooms.every((room) => room.notApplicable);
  const [fora, setFora] = useState<boolean | undefined>(undefined);
  const valor = lerPercentual(texto);
  const ficaFora = (room: FrontRoomView): boolean => fora ?? room.notApplicable;
  // O dia do apontamento (23/10/2026): hoje, ou um dia que passou.
  const [dia, setDia] = useState(date);
  const diaValido = /^\d{4}-\d{2}-\d{2}$/.test(dia) && dia <= date;

  const [gravar, estado] = useCommand(
    async () => {
      if (session === undefined || activeProjectId === undefined) {
        return ok({ queued: false });
      }
      // O "não se aplica" antes, uma vez por cômodo da tipologia: o percentual de um cômodo fora
      // da conta seria recusado.
      const marcas = new Map<string, boolean>();
      for (const room of rooms) {
        if (fora !== undefined && fora !== room.notApplicable) marcas.set(room.roomId, fora);
      }
      const apontar =
        valor === null || valor === undefined
          ? []
          : rooms
              .filter((room) => !ficaFora(room) && room.percent !== valor)
              .map((room) => ({
                positionId: room.positionId,
                roomId: room.roomId,
                percent: valor,
              }));
      // Uma operação só, que sobe na hora ou fica no aparelho sem sinal (RN-614).
      const feito = await container.outbox.submit(
        {
          kind: 'comodos',
          projectId: activeProjectId,
          date: dia,
          resourceId: frenteNaFila(activityId, serviceId),
          activityId,
          serviceId,
          notApplicable: [...marcas].map(([roomId, notApplicable]) => ({ roomId, notApplicable })),
          progress: apontar,
        },
        session,
      );
      return feito.ok ? ok({ queued: feito.value.queued }) : err(feito.error);
    },
    {
      successMessage: (resultado) =>
        resultado.queued
          ? GUARDADO_NO_APARELHO
          : rooms.length === 1
            ? `${primeiro.roomName} · ${nomeDoApto(primeiro.positionLabel)} gravado.`
            : `${String(rooms.length)} cômodos gravados.`,
      onSuccess: onDone,
    },
  );

  const mudaMarca = fora !== undefined && rooms.some((room) => fora !== room.notApplicable);
  const mudaPercentual =
    valor !== null &&
    valor !== undefined &&
    rooms.some((room) => !ficaFora(room) && room.percent !== valor);
  const sozinho = rooms.length === 1 ? primeiro : undefined;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        void gravar();
      }}
    >
      <p className="text-sm">
        {nomesEscolhidos(
          rooms.map((room) => `${room.roomName} · ${nomeDoApto(room.positionLabel)}`),
        )}
        {sozinho !== undefined && (
          <span className="block text-xs text-(--color-content-muted)">
            {sozinho.unitTypeName}
            {sozinho.recordedOn === undefined
              ? ' · sem apontamento'
              : ` · ${percentual(sozinho.percent ?? 0)} em ${diaEMes(sozinho.recordedOn)}`}
          </span>
        )}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {ATALHOS.map((atalho) => (
          <Button
            key={atalho}
            type="button"
            size="sm"
            variant={valor === atalho ? 'primary' : 'secondary'}
            disabled={!canMark || fora === true || estado.isPending}
            onClick={() => setTexto(String(atalho))}
          >
            {atalho}%
          </Button>
        ))}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <Input
          aria-label="Percentual dos escolhidos"
          inputMode="decimal"
          placeholder={iguais ? undefined : 'vários'}
          className={cn(
            'h-9 w-24 text-right tabular',
            valor === null && 'border-(--color-state-danger)',
          )}
          value={texto}
          disabled={!canMark || fora === true || estado.isPending}
          aria-invalid={valor === null || undefined}
          onChange={(event) => setTexto(event.target.value)}
        />
        %
      </label>
      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-1"
          checked={fora ?? todosFora}
          disabled={!canMark || estado.isPending}
          onChange={(event) => setFora(event.target.checked)}
        />
        <span>
          Não se aplica
          <span className="block text-xs text-(--color-content-muted)">
            Vale para o cômodo em todos os aptos da mesma tipologia.
          </span>
        </span>
      </label>
      {valor === null && (
        <p role="alert" className="text-xs text-(--color-state-danger)">
          O percentual vai de 0 a 100.
        </p>
      )}
      {canMark && (
        <label className="flex items-center gap-2 text-sm">
          Dia
          <Input
            type="date"
            aria-label="Dia do apontamento"
            className="h-9 w-40"
            value={dia}
            max={date}
            disabled={estado.isPending}
            aria-invalid={!diaValido || undefined}
            onChange={(event) => setDia(event.target.value)}
          />
        </label>
      )}
      {!diaValido && (
        <p role="alert" className="text-xs text-(--color-state-danger)">
          Escolha hoje ou um dia que já passou.
        </p>
      )}
      {canMark && (
        <Button
          type="submit"
          disabled={
            (!mudaMarca && !mudaPercentual) || valor === null || !diaValido || estado.isPending
          }
        >
          {estado.isPending
            ? 'Gravando…'
            : rooms.length === 1
              ? 'Gravar'
              : `Gravar nos ${String(rooms.length)}`}
        </Button>
      )}
    </form>
  );
}

export function MarcarPecas({
  escolhidas,
  activityId,
  serviceId,
  date,
  canMark,
  onDone,
}: {
  readonly escolhidas: readonly FrontPieceView[];
  readonly activityId: string;
  readonly serviceId: string;
  readonly date: string;
  readonly canMark: boolean;
  readonly onDone: () => void;
}) {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  // O dia da marca (23/10/2026): hoje, ou um dia que passou — a estaca furada ontem se marca
  // hoje com a data de ontem. Dia que ainda não chegou, não.
  const [dia, setDia] = useState(date);
  const diaValido = /^\d{4}-\d{2}-\d{2}$/.test(dia) && dia <= date;
  const quando = dia === date ? 'hoje' : `em ${diaEMes(dia)}`;

  const aMarcar = escolhidas.filter((piece) => estadoDaPeca(piece) === 'NAO_INICIADA');
  const jaMarcadas = escolhidas.filter((piece) => piece.executedOn !== undefined);
  // A marcada noutro dia não se marca de novo (RN-381): para trocar a data, ela sai do dia
  // dela e entra no dia escolhido.
  const deOutroDia = jaMarcadas.filter((piece) => piece.executedOn !== dia);

  /**
   * O que muda em cada dia, numa operação só: sobe na hora ou fica no aparelho (RN-614).
   *
   * A mudança, e não a lista inteira do dia: a lista do servidor substitui as marcas daquele dia
   * (D22), e a que o aparelho guardou sem sinal desfaria as marcas que outra pessoa fez nesse
   * meio tempo. A operação lê a lista do servidor ao subir e aplica só o que mudou.
   */
  const enviarMudancas = async (
    changes: readonly { date: string; add: readonly string[]; remove: readonly string[] }[],
  ) => {
    if (session === undefined || activeProjectId === undefined) {
      return ok({ queued: false, marked: 0, unmarked: 0 });
    }
    const feito = await container.outbox.submit(
      {
        kind: 'pecas-da-frente',
        projectId: activeProjectId,
        date: dia,
        resourceId: frenteNaFila(activityId, serviceId),
        activityId,
        serviceId,
        changes,
      },
      session,
    );
    if (!feito.ok) return err(feito.error);
    return ok(
      feito.value.queued
        ? { queued: true, marked: 0, unmarked: 0 }
        : { queued: false, ...(feito.value.outcome.pieceMarks ?? { marked: 0, unmarked: 0 }) },
    );
  };

  /** Tira as peças dos dias em que estão marcadas — uma mudança por dia. */
  const tirarDosDias = (tirar: readonly FrontPieceView[]) => {
    const dias = [...new Set(tirar.map((piece) => piece.executedOn!))];
    return dias.map((data) => ({
      date: data,
      add: [],
      remove: tirar.filter((piece) => piece.executedOn === data).map((piece) => piece.name),
    }));
  };

  const [marcar, marcando] = useCommand(
    () => enviarMudancas([{ date: dia, add: aMarcar.map((piece) => piece.name), remove: [] }]),
    {
      successMessage: (resultado) =>
        resultado.queued
          ? GUARDADO_NO_APARELHO
          : resultado.marked === 1
            ? `1 peça marcada ${quando}.`
            : `${String(resultado.marked)} peças marcadas ${quando}.`,
      onSuccess: onDone,
    },
  );
  const [desmarcar, desmarcando] = useCommand(() => enviarMudancas(tirarDosDias(jaMarcadas)), {
    successMessage: (resultado) =>
      resultado.queued
        ? GUARDADO_NO_APARELHO
        : resultado.unmarked === 1
          ? '1 peça desmarcada.'
          : `${String(resultado.unmarked)} peças desmarcadas.`,
    onSuccess: onDone,
  });
  const [mudarData, mudandoData] = useCommand(
    () =>
      enviarMudancas([
        ...tirarDosDias(deOutroDia),
        { date: dia, add: deOutroDia.map((piece) => piece.name), remove: [] },
      ]),
    {
      successMessage: (resultado) =>
        resultado.queued
          ? GUARDADO_NO_APARELHO
          : deOutroDia.length === 1
            ? `${deOutroDia[0]!.name} passou para ${diaEMes(dia)}.`
            : `${String(deOutroDia.length)} peças passaram para ${diaEMes(dia)}.`,
      onSuccess: onDone,
    },
  );
  const ocupado = marcando.isPending || desmarcando.isPending || mudandoData.isPending;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm">
        {nomesEscolhidos(escolhidas.map((piece) => piece.name))}
        {escolhidas.length === 1 && escolhidas[0]!.executedOn !== undefined && (
          <span className="block text-xs text-(--color-content-muted)">
            Executada em {diaEMes(escolhidas[0]!.executedOn)}
          </span>
        )}
      </p>
      {canMark && (
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-sm">
            Dia
            <Input
              type="date"
              aria-label="Dia da marca"
              className="h-9 w-40"
              value={dia}
              max={date}
              disabled={ocupado}
              aria-invalid={!diaValido || undefined}
              onChange={(event) => setDia(event.target.value)}
            />
          </label>
          {!diaValido && (
            <p role="alert" className="text-xs text-(--color-state-danger)">
              Escolha hoje ou um dia que já passou.
            </p>
          )}
          {aMarcar.length > 0 && (
            <Button type="button" disabled={!diaValido || ocupado} onClick={() => void marcar()}>
              {aMarcar.length === 1
                ? `Marcar 1 como executada ${quando}`
                : `Marcar ${String(aMarcar.length)} como executadas ${quando}`}
            </Button>
          )}
          {deOutroDia.length > 0 && (
            <Button
              type="button"
              variant="secondary"
              disabled={!diaValido || ocupado}
              onClick={() => void mudarData()}
            >
              {deOutroDia.length === 1
                ? `Passar a data para ${diaEMes(dia)}`
                : `Passar a data das ${String(deOutroDia.length)} para ${diaEMes(dia)}`}
            </Button>
          )}
          {jaMarcadas.length > 0 && (
            <Button
              type="button"
              variant="secondary"
              disabled={ocupado}
              onClick={() => void desmarcar()}
            >
              {jaMarcadas.length === 1
                ? 'Desmarcar (não executada)'
                : `Desmarcar as ${String(jaMarcadas.length)} (não executadas)`}
            </Button>
          )}
        </div>
      )}
      <p className="text-xs text-(--color-content-muted)">
        A marca é o avanço da peça; a quantidade produzida no dia continua no apontamento.
      </p>
    </div>
  );
}
